import { Injectable } from '@angular/core';
import { CaeItem, CaeRail } from '@project/cae/cae-data.service';
import { ArticleConstants } from '@article/constants/article-constants';
import { CaeProjectHelper } from '@project/processor/cae/cae-project-helper';
import { ArticleManager } from '@article/manager/article.manager';
import { CarrierBlockVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { CaeProjectImportUtils } from '@project/cae/util/cae-project-import-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { MountingRailCreationModel } from '../../../wizard/service/mounting-rail-creation.model';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { CustomArticleInfoVO } from '@project/components/article/model/vo/custom-article.vo';
import { CustomArticleService } from '@article/service/custom-article.service';
import { UserModel } from '@user/model/user.model';
import { OnRailReferenceArticleLocationVO } from '@project/components/article/model/vo/on-rail-reference-article-location.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { NumberUtils } from '@shared/util/number-utils';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { ProjectArticleService } from '@project/service/project-article.service';
import { InsertArticleService } from '@project/components/article/service/insert-article.service';
import { InsertedArticleData } from '@project/components/article/processor/i-article-location.processor';
import { CarrierBlockUtils } from '@project/components/article/util/carrier-block-utils';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { CaeImportHelperService } from './cae-import-helper.service';
import { CaeImportOptions } from './cae-import-options.service';
import { CaeImportErrorCollector } from '../cae-import-error-collector';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { CaeRailsData } from './cae-rails-data';

type ArticleOptions = Pick<CaeImportOptions,
  | 'startProjection'
  | 'openLeftSide'
  | 'groupDistance'
  | 'addEndstopBmk'
>;

@Injectable({
  providedIn: 'root'
})
export class CaeImportArticlesOnRailService {

  private caeImportErrorCollector!: CaeImportErrorCollector;

  constructor(
    private caeProjectHelper: CaeProjectHelper,
    private articleManager: ArticleManager,
    private projectModel: ProjectModel,
    private customArticleService: CustomArticleService,
    private userModel: UserModel,
    private containerService: ContainerService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private projectArticleService: ProjectArticleService,
    private insertArticleService: InsertArticleService,
    private compositeArticleUtils: CompositeArticleUtils,
    private caeImportHelperService: CaeImportHelperService,
    private undoRedoHelper: UndoRedoHelper,
  ) { }

  public async addArticles(caeRailsData: CaeRailsData, options: ArticleOptions, caeImportErrorCollector: CaeImportErrorCollector): Promise<void> {

    this.caeImportErrorCollector = caeImportErrorCollector;

    for (let index = 0; index < caeRailsData.currentRails.length; index++) {
      const caeRail = caeRailsData.currentRails[index];
      this.caeImportErrorCollector.processRail(caeRail);
      await this.retrieveArticlesForCarrierGroup(caeRail, options.addEndstopBmk);
      this.caeProjectHelper.prepareCaeProject(caeRail, options.openLeftSide);
      if (options.addEndstopBmk) {
        this.caeProjectHelper.prepareBmkEndstops(caeRail);
      }
      this.storeCarrierBlockInfoToCurrentProject(caeRail);
      await this.attachArticlesOnRail(caeRail.items, index === 0 ? options.startProjection : options.groupDistance, options.openLeftSide, caeRail.id);
    }
  }

  private async retrieveArticlesForCarrierGroup(caeRail: CaeRail, addEndstopBmk: boolean): Promise<void> {

    const articleNumbersToLoad: string[] = [];
    articleNumbersToLoad.push(...this.getArticleNumberByAccessories(caeRail.accessory));
    articleNumbersToLoad.push(ArticleConstants.CUSTOM_ARTICLE_NO);

    for (const caeItem of caeRail.items) {
      const articleNumber: string = this.caeProjectHelper.getArticleNumberCleared(caeItem.articleNo);
      if (articleNumber && articleNumbersToLoad.indexOf(articleNumber) < 0) {
        articleNumbersToLoad.push(articleNumber);
      }
      if (!articleNumber) {
        this.caeImportErrorCollector.addErrorForItem(caeItem, 'caeImport.common.error.article_number_missing');
      }
      articleNumbersToLoad.push(...this.getArticleNumberByAccessories(caeItem.items));
    }

    if (addEndstopBmk) {
      articleNumbersToLoad.push(CaeProjectHelper.DEFAULT_ENDSTOP_ARTICLENO);
      articleNumbersToLoad.push(CaeProjectHelper.DEFAULT_GROUP_MARKER_ARTICLENO);
      articleNumbersToLoad.push(CaeProjectHelper.DEFAULT_LABELSTRIP_ARTICLENO);
    }

    await this.articleManager.loadArticlesByArticleNumbers(articleNumbersToLoad);
  }

  private getArticleNumberByAccessories(accessories: CaeItem[]): string[] {
    if (!accessories) {
      return [];
    }

    const articleNos: string[] = [];
    for (const accessory of accessories) {
      const articleNumber: string = this.caeProjectHelper.getArticleNumberCleared(accessory.articleNo);
      if (articleNumber && articleNos.indexOf(articleNumber) < 0) {
        articleNos.push(articleNumber);
      }
      if (!articleNumber) {
        this.caeImportErrorCollector.addErrorForItem(accessory, 'caeImport.common.error.article_number_missing');
      }
    }

    return articleNos;
  }

  private storeCarrierBlockInfoToCurrentProject(rail: CaeRail): void {
    const carrierBlock: CarrierBlockVO = new CarrierBlockVO();
    carrierBlock.guid = rail.id;
    carrierBlock.name = rail.name;
    carrierBlock.structure = rail.structure;
    carrierBlock.articleGuids = [];
    if (!this.projectModel.currentProject.carrierBlocks) {
      this.projectModel.currentProject.carrierBlocks = [];
    }
    this.projectModel.currentProject.carrierBlocks.push(carrierBlock);
  }

  private async attachArticlesOnRail(caeItems: CaeItem[], articlesOffset: number, openLeftSide: boolean, carrierBlockId: string): Promise<void> {
    for (const caeItem of caeItems) {
      const success = await this.attachArticleOnRail(caeItem, articlesOffset, openLeftSide, carrierBlockId);
      if (success) {
        articlesOffset = 0;
      }
    }
    this.updateOpenSideOnRailForCarrier(0, openLeftSide);
  }

  private async attachArticleOnRail(caeItem: CaeItem, articlesOffset: number, openSideLeft: boolean, carrierBlockId: string): Promise<boolean> {

    const article = await this.getArticleForCaeItem(caeItem);
    const caeArticleSpace = articlesOffset + (caeItem.space ? caeItem.space : 0);
    const caeArticleOffsetX = caeItem.offset.x ? caeItem.offset.x : 0;

    const articleLocation: ArticleLocationVO = this.createOnRailReference(caeArticleSpace, caeArticleOffsetX);
    if (CaeProjectImportUtils.isCaeArticleCustomType(caeItem)) {
      await this.processArticleLocationByCustomArticle(
        article,
        caeItem,
        articleLocation,
      );
    } else if (ArticleUtils.isEmptyZoneArticle(article)) {
      this.processArticleLocationByEmptyZone(article, caeItem, articleLocation);
    }

    this.updateOpenSideOnRailForCarrier(caeItem.rotation, openSideLeft);
    try {
      const insertedArticleData: InsertedArticleData = await this.insertArticleService.insertArticleByArticleLocation(article, articleLocation, 'caeProjectImportAttachArticleToRail');
      this.articleInserted(insertedArticleData, caeItem, carrierBlockId);
      return true;
    } catch (error) {
      this.caeImportErrorCollector.addErrorForItem(caeItem, 'caeImport.common.error.could_not_add_article');
      return false;
    }
  }

  private async getArticleForCaeItem(caeItem: CaeItem): Promise<ArticleVO | undefined> {
    let article: ArticleVO | undefined = this.caeProjectHelper.getArticleByCaeBaseArticle(caeItem);
    if (!article) {
      if (!await this.findCustomArticleInfoForArticle(caeItem.articleNo)) {
        this.caeImportErrorCollector.addErrorForItem(caeItem, 'caeImport.common.error.article_not_found', [ caeItem.articleNo ]);
      }
      caeItem.type = 'custom';
      article = this.caeProjectHelper.getArticleByCaeBaseArticle(caeItem);
    }
    return article;
  }

  private async findCustomArticleInfoForArticle(customArticleNumber: string): Promise<CustomArticleInfoVO | undefined> {
    const customLibrary = await this.customArticleService.getOwnCustomArticleLibrary(this.userModel.user.id);
    return customLibrary.customArticleInfos.find((customArticleInfo) => customArticleInfo.articleNumber === customArticleNumber);
  }

  private updateOpenSideOnRailForCarrier(rotation: number, openSideLeft: boolean): void {
    const mountingRailArticleUsage = ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject);
    if (rotation === 180 || openSideLeft) {
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(
        mountingRailArticleUsage,
        ArticleUsageInfoPropertyDef.MOUNTING_RAIL_OPEN_SIDE,
        MountingRailCreationModel.LEFT_OPEN_SIDE,
      );
    } else {
      ArticleUsageUtils.removeArticleUsageInfoProperty(mountingRailArticleUsage, ArticleUsageInfoPropertyDef.MOUNTING_RAIL_OPEN_SIDE);
    }
  }

  private createOnRailReference(articleSpace: number, articleOffsetX: number): ArticleLocationVO {
    const mountingRailArticleUsage = ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject);
    const offset = this.findBestOffset(articleSpace, articleOffsetX);
    const articleLocation: OnRailReferenceArticleLocationVO = new OnRailReferenceArticleLocationVO();
    articleLocation.rangeOnLineArticleUsage = mountingRailArticleUsage;
    articleLocation.articleOffset = offset;
    return articleLocation;
  }

  private findBestOffset(articleSpace: number, articleOffsetX: number): number {
    const mountingRailArticleUsage = ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject);
    const articleSpace3D = this.caeImportHelperService.millimetersToAway3DUnits(articleSpace);
    const articleOffsetX3D = this.caeImportHelperService.millimetersToAway3DUnits(articleOffsetX);
    const rangeOnLineContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(mountingRailArticleUsage.id);
    const insertHelper = this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(rangeOnLineContainer);
    const calcOffset = articleSpace3D + insertHelper.calculateOffsetToLastArticle();

    if (articleOffsetX3D === 0) {
      return calcOffset;
    }

    if (calcOffset !== 0 && Math.abs(calcOffset - articleOffsetX3D) < 20) {
      return calcOffset;
    }

    return Math.max(calcOffset, articleOffsetX3D);
  }

  private async processArticleLocationByCustomArticle(customArticle: ArticleVO, customCaeArticle: CaeItem, articleLocation: ArticleLocationVO): Promise<void> {
    this.validateCustomArticle(customCaeArticle);

    const articleUsageToCreate: ArticleUsageVO = this.projectArticleService.createNewArticleUsage(customArticle.identity, customArticle.version);
    let customArticleInfo = await this.findCustomArticleInfoForArticle(customCaeArticle.articleNo ? customCaeArticle.articleNo : customCaeArticle.id);
    if (!customArticleInfo) {
      customArticleInfo = new CustomArticleInfoVO();
      customArticleInfo.articleNumber = customCaeArticle.articleNo ? customCaeArticle.articleNo : customCaeArticle.id;
      customArticleInfo.articleDescription = customCaeArticle.id;
      customArticleInfo.articleWidth = this.caeImportHelperService.millimetersToAway3DUnits(customCaeArticle.size.width);
      customArticleInfo.articleDepth = this.caeImportHelperService.millimetersToAway3DUnits(customCaeArticle.size.height);
      customArticleInfo.articleHeight = this.caeImportHelperService.millimetersToAway3DUnits(customCaeArticle.size.depth);
      customArticleInfo.articleDepthOffset = 0;
    }
    CustomArticleUtils.applyCustomArticleInfoToArticleUsage(articleUsageToCreate, customArticleInfo);
    articleLocation.existingArticleUsageId = articleUsageToCreate.id;
    articleLocation.checkConnectionProperties = false;
  }

  private validateCustomArticle(caeItem: CaeItem) {
    if (!this.isSizeValueValid(caeItem.size.width)) {
      caeItem.size.width = 20;
      this.caeImportErrorCollector.addErrorForItem(caeItem, 'caeImport.custom_article.default_width_used');
    }
    if (!this.isSizeValueValid(caeItem.size.depth)) {
      caeItem.size.depth = 100;
      this.caeImportErrorCollector.addErrorForItem(caeItem, 'caeImport.custom_article.default_depth_used');
    }
    if (!this.isSizeValueValid(caeItem.size.height)) {
      caeItem.size.height = 100;
      this.caeImportErrorCollector.addErrorForItem(caeItem, 'caeImport.custom_article.default_height_used');
    }
  }

  private isSizeValueValid(value: number): boolean {
    return NumberUtils.isNumber(value) && value > 0;
  }

  private processArticleLocationByEmptyZone(emptyZone: ArticleVO, customCaeItem: CaeItem, articleLocation: ArticleLocationVO) {
    if (!this.isSizeValueValid(customCaeItem.size.width)) {
      customCaeItem.size.width = 20;
      this.caeImportErrorCollector.addErrorForItem(customCaeItem, 'caeImport.emptyZone.default_length_used');
    }

    const articleUsageToCreate: ArticleUsageVO = this.projectArticleService.createNewArticleUsage(emptyZone.identity, emptyZone.version);
    const customArticleInfo: CustomArticleInfoVO = new CustomArticleInfoVO();
    customArticleInfo.articleNumber = emptyZone.articleNo;
    customArticleInfo.articleWidth = this.caeImportHelperService.millimetersToAway3DUnits(customCaeItem.size.width);
    CustomArticleUtils.applyCustomArticleInfoToArticleUsage(articleUsageToCreate, customArticleInfo);
    articleLocation.existingArticleUsageId = articleUsageToCreate.id;
    articleLocation.checkConnectionProperties = false;
  }

  private articleInserted(insertedArticleData: InsertedArticleData, caeItem: CaeItem, carrierBlockGuid: string): void {

    const baseGuid: string = insertedArticleData.baseArticleGUID;
    const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(baseGuid);
    this.caeImportHelperService.replaceArticleGuid(articleUsage, caeItem.id);

    this.caeImportHelperService.writeArticleNoWithPrefixIntoUsage(articleUsage, caeItem.articleNo);
    this.caeImportHelperService.writeArticleInfoIntoUsage(articleUsage, caeItem.name, ArticleUsageInfoPropertyDef.CAE_ITEM_NAME);

    const carrierBlock: CarrierBlockVO = CarrierBlockUtils.getCarrierBlockInProjectByGuid(this.projectModel.currentProject, carrierBlockGuid);
    CarrierBlockUtils.applyCarrierBlockGuidByArticleUsage(articleUsage, carrierBlock, this.compositeArticleUtils, caeItem.structure);

    insertedArticleData.baseArticleGUID = caeItem.id;
    for (let i = 0; i < insertedArticleData.insertedArticleGUIDs.length; i++) {
      if (insertedArticleData.insertedArticleGUIDs[i] === baseGuid) {
        insertedArticleData.insertedArticleGUIDs[i] = caeItem.id;
      }
    }
    this.undoRedoHelper.pushInsertArticleByLocationUndoOperation(insertedArticleData);
  }
}
